/**
 * Readiness API Client
 *
 * Communicates with the orchestration service readiness endpoints
 * to fetch epic readiness assessments, findings, and resolution actions.
 */

const API_BASE = '/api/orchestration';

export type FindingSeverity = 'critical' | 'warning' | 'info';

export interface ReadinessFinding {
  severity: FindingSeverity;
  category: string;
  message: string;
  suggestion: string;
  issueId?: string;
}

export interface IssueReadiness {
  issueId: string;
  issueTitle: string;
  issueType: string;
  requirementsScore: number;
  acceptanceCriteriaScore: number;
  findings: ReadinessFinding[];
  resolutionPrompt: string;
}

export interface DimensionResult {
  dimension: string;
  score: number;
  weight: number;
  status: 'green' | 'amber' | 'red';
  details: string;
  checkedAt?: string;
  findings?: ReadinessFinding[];
  issueBreakdown?: IssueReadiness[];
}

export interface ReadinessAssessment {
  epicId: string;
  projectId: string;
  overallScore: number;
  overallStatus: 'green' | 'amber' | 'red';
  dimensions: DimensionResult[];
  assessedAt: string;
  stale: boolean;
  issueReadiness: IssueReadiness[];
  criticalFindings: ReadinessFinding[];
  resolutionSummary: string;
}

export interface ResolvedIssue {
  issueId: string;
  changes: {
    description?: { before: string; after: string };
    acceptanceCriteria?: { before: string[]; after: string[] };
  };
  written: boolean;
}

export const dimensionLabel: Record<string, string> = {
  requirementsClarity: 'Requirements Clarity',
  acceptanceCriteria: 'Acceptance Criteria',
  technicalContext: 'Technical Context',
  testStrategy: 'Test Strategy',
  implementationReadiness: 'Implementation Readiness',
  // Legacy dimension names (backward compat)
  specCompleteness: 'Spec Completeness',
  interfaceContracts: 'Interface Contracts',
  testPlanCoverage: 'Test Plan Coverage',
  storyDecomposition: 'Story Decomposition',
  environmentReadiness: 'Environment Readiness',
  dependencyResolution: 'Dependencies',
  storyReadiness: 'Story Readiness',
  repositoryConfig: 'Repository Config',
  specDocumentation: 'Spec Documentation',
  epicClarity: 'Epic Clarity',
};

export const readinessApi = {
  /**
   * Get the latest readiness assessment for an epic.
   * Pass refresh=true to force a fresh assessment.
   */
  async getEpicReadiness(epicId: string, refresh?: boolean): Promise<ReadinessAssessment> {
    const url = `${API_BASE}/readiness/epics/${epicId}${refresh ? '?refresh=true' : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to get readiness: ${res.statusText}`);
    return res.json();
  },

  /**
   * Trigger a new readiness assessment for an epic.
   */
  async assessEpic(epicId: string, projectId: string): Promise<ReadinessAssessment> {
    const res = await fetch(`${API_BASE}/readiness/epics/${epicId}/assess`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId }),
    });
    if (!res.ok) throw new Error(`Failed to assess: ${res.statusText}`);
    return res.json();
  },

  /**
   * Get readiness assessments for all epics in a project.
   */
  async getProjectEpics(projectId: string): Promise<{ epics: ReadinessAssessment[] }> {
    const res = await fetch(`${API_BASE}/readiness/projects/${projectId}/epics`);
    if (!res.ok) throw new Error(`Failed to get project epics: ${res.statusText}`);
    return res.json();
  },

  /**
   * Get historical readiness assessments for an epic.
   */
  async getHistory(epicId: string): Promise<{ history: ReadinessAssessment[] }> {
    const res = await fetch(`${API_BASE}/readiness/epics/${epicId}/history`);
    if (!res.ok) throw new Error(`Failed to get history: ${res.statusText}`);
    return res.json();
  },

  /**
   * Get aggregated findings for an epic.
   */
  async getFindings(epicId: string): Promise<{
    criticalFindings: ReadinessFinding[];
    dimensionFindings: (ReadinessFinding & { dimension: string })[];
    issueFindings: (ReadinessFinding & { issueTitle: string })[];
    totalCount: number;
    criticalCount: number;
    resolutionSummary: string;
  }> {
    const res = await fetch(`${API_BASE}/readiness/epics/${epicId}/findings`);
    if (!res.ok) throw new Error(`Failed to get findings: ${res.statusText}`);
    return res.json();
  },

  /**
   * Get the resolution prompt for a specific issue.
   */
  async getResolutionPrompt(epicId: string, issueId: string): Promise<{
    issueTitle: string;
    requirementsScore: number;
    acceptanceCriteriaScore: number;
    findings: ReadinessFinding[];
    resolutionPrompt: string;
  }> {
    const res = await fetch(`${API_BASE}/readiness/epics/${epicId}/issues/${issueId}/prompt`);
    if (!res.ok) throw new Error(`Failed to get resolution prompt: ${res.statusText}`);
    return res.json();
  },

  /**
   * Run agent-powered fix for an issue.
   */
  async fixIssue(epicId: string, issueId: string, projectId: string): Promise<ResolvedIssue> {
    const res = await fetch(`${API_BASE}/readiness/epics/${epicId}/issues/${issueId}/fix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId }),
    });
    if (!res.ok) throw new Error(`Failed to fix issue: ${res.statusText}`);
    return res.json();
  },
};
